import React from 'react';
import TablyMark from '../components/tably/TablyMark';
import TablyStepArt from '../components/tably/TablyStepArt';
import TablyPhoneMockup from '../components/tably/TablyPhoneMockup';
import './TablyPage.scss';

const STEPS = [
  {
    n: 1,
    title: 'Arma tu tably',
    description:
      'Configura tu lugar, sube tu menú y listo. Tu carta digital queda con cara de app — pensada para que tus clientes la usen desde el celular como si fuera nativa.',
  },
  {
    n: 2,
    title: 'Imprime tus QR y a pegarlos',
    description:
      'Cada código está casado con una mesa, una barra, la terraza o el espacio que tú decidas. Así sabes exactamente desde dónde te están pidiendo.',
  },
  {
    n: 3,
    title: 'Tus clientes escanean y listo',
    description:
      'Abren el menú, escogen lo que quieren, dejan sus notas y pagan en el momento. Sin levantar la mano, sin descargar nada, sin esperas.',
  },
  {
    n: 4,
    title: 'La orden cae directo a tu cocina',
    description:
      'Llega a tu POS y a las impresoras al instante. Tu equipo se concentra en cocinar y servir, no en correr a levantar pedidos.',
  },
] as const;

type Benefit = { text: string; icon: React.ReactNode };

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const BENEFITS: Benefit[] = [
  {
    text: 'Pagos fáciles con Apple Pay, Google Pay y Square Pay.',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="2.5" y="6" width="19" height="13" rx="2.5" />
        <line x1="2.5" y1="10" x2="21.5" y2="10" />
        <line x1="6" y1="15" x2="9" y2="15" />
        <line x1="11" y1="15" x2="13" y2="15" />
      </svg>
    ),
  },
  {
    text: 'Rotación más rápida de mesas. Pedir y pagar toma minutos, no media hora.',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M21 12a9 9 0 1 1-3-6.7" />
        <polyline points="21 4 21 9 16 9" />
      </svg>
    ),
  },
  {
    text: 'Tu equipo se concentra en servir, no en levantar pedidos.',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3.5" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    text: 'Tus clientes piden cuando ellos quieren, sin esperar a nadie.',
    icon: (
      <svg {...ICON_PROPS}>
        <circle cx="12" cy="12" r="9" />
        <polyline points="12 7 12 12 15.5 14" />
      </svg>
    ),
  },
  {
    text: 'Menú móvil claro que hace que pedir sea rapidísimo.',
    icon: (
      <svg {...ICON_PROPS}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <line x1="11" y1="18.5" x2="13" y2="18.5" />
      </svg>
    ),
  },
  {
    text: 'Datos reales y campañas para convertir visitantes en clientes recurrentes.',
    icon: (
      <svg {...ICON_PROPS}>
        <polyline points="3 17 9 11 13 15 21 7" />
        <polyline points="15 7 21 7 21 13" />
      </svg>
    ),
  },
  {
    text: 'Pedidos consolidados por mesa para que la cocina tenga claridad total.',
    icon: (
      <svg {...ICON_PROPS}>
        <path d="M5 3.5h14v17l-2.5-1.5-2.5 1.5-2-1.5-2 1.5-2.5-1.5L5 20.5z" />
        <line x1="8.5" y1="9" x2="15.5" y2="9" />
        <line x1="8.5" y1="13" x2="13.5" y2="13" />
      </svg>
    ),
  },
];

const TablyPage: React.FC = () => {
  return (
    <div className="tably-page">
      {/* HERO */}
      <section className="tably-hero">
        <div className="tably-hero__glow" aria-hidden="true" />
        <div className="container tably-hero__grid">
          <div className="tably-hero__content">
            <div className="tably-hero__brand">
              <TablyMark size="lg" />
            </div>
            <h1 className="tably-hero__title">Tu mesa es la nueva caja.</h1>
            <p className="tably-hero__sub">
              Tus clientes escanean un QR, ven el menú, ordenan y pagan con tarjeta desde su
              celular. Tú dejas de perder mesas, propinas y tiempo.
            </p>
          </div>

          <div className="tably-hero__media">
            <TablyPhoneMockup />
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="tably-steps">
        <div className="container">
          <div className="tably-steps__header">
            <p className="tably-steps__label">Cómo funciona</p>
            <h2 className="tably-steps__title">
              Cuatro pasos. <span className="tably-coral">Cero fricción.</span>
            </h2>
          </div>

          <div className="tably-steps__list">
            {STEPS.map((s, i) => (
              <div key={s.n} className={`tably-step ${i % 2 ? 'tably-step--reverse' : ''}`}>
                <div className="tably-step__art">
                  <TablyStepArt step={s.n as 1 | 2 | 3 | 4} />
                </div>
                <div className="tably-step__text">
                  <span className="tably-step__number">Paso {s.n}</span>
                  <h3 className="tably-step__title">{s.title}</h3>
                  <p className="tably-step__description">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="tably-benefits">
        <div className="container">
          <div className="tably-benefits__header">
            <p className="tably-benefits__label">Beneficios</p>
            <h2 className="tably-benefits__title">
              Más eficiencia. <span className="tably-coral">Mejor experiencia.</span>
            </h2>
          </div>

          <ul className="tably-benefits__list">
            {BENEFITS.map((b) => (
              <li key={b.text} className="tably-benefit">
                <span className="tably-benefit__icon" aria-hidden="true">{b.icon}</span>
                <p>{b.text}</p>
              </li>
            ))}
          </ul>

          <div className="tably-benefits__cta-row">
            <a
              href="https://tably.mx"
              target="_blank"
              rel="noopener noreferrer"
              className="tably-benefits__cta"
            >
              Únete a la mesa
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TablyPage;
